// geographycontent1.js
// Grade9Ready — Geography Section 1: Locational Knowledge
//
// Production-ready section file:
// - Mirrors English content structure
// - Builds KS3 foundation into GCSE Geography geographical skills
// - Covers continents, oceans, latitude/longitude, Africa, India, China, Russia and the Middle East
// - Adds consistent metadata, keywords, quickCheck, commonMistakes and examSkills
// - Normalises question types: mcq / true-false / short-answer
// - Includes worked examples, application tasks, AI feedback, improvement and mastery tasks

const GEOGRAPHY_SKILLS = {
  LOCATIONAL:
    'Locational knowledge: identify globally significant places, regions, countries, oceans and major physical/human features',
  MAP_SKILLS:
    'Map skills: use atlases, globes, latitude, longitude, compass directions, scale and spatial patterns',
  PLACE:
    'Place knowledge: understand similarities, differences and links between places and regions',
  PHYSICAL:
    'Physical geography: describe major natural features including mountains, rivers, deserts, climate zones and biomes',
  HUMAN:
    'Human geography: describe population, settlement, economic activity, resources and development patterns',
  INTERCONNECTION:
    'Interconnection: explain how places are linked through trade, migration, resources, climate and global systems',
  DATA:
    'Data and evidence: use maps, graphs, statistics, images and written sources to support geographical explanations',
  EXAM:
    'Exam skill: use precise place evidence, accurate terminology and structured explanation',
};

const sectionCommonMistakes = [
  'Confusing continents with countries',
  'Confusing latitude and longitude',
  'Forgetting that latitude is measured north or south of the Equator',
  'Forgetting that longitude is measured east or west of the Prime Meridian',
  'Using vague locations such as “near Africa” instead of naming countries, regions or compass directions',
  'Naming a place without explaining why its location matters',
  'Treating physical geography and human geography as separate when they often interact',
];

const tier = (label, text, critique) => ({ label, text, critique });

const makeTopic = (topic) => ({
  duration: '30-45 mins',
  xp: 120,
  difficulty: 'foundation-to-grade-9',
  gradeTarget: topic.gradeTarget || '4-9',
  stretchTarget: topic.stretchTarget || '8-9',
  curriculumStage: 'KS3 locational knowledge into GCSE Geography skills',
  examBoardFit: ['AQA 8035', 'Edexcel B', 'OCR B', 'Eduqas/WJEC'],
  courseLevel: topic.courseLevel || 'KS3 bridge into GCSE',
  aqaPaper: topic.aqaPaper || 'Geographical skills / foundation knowledge',
  examSkills: topic.examSkills || [
    GEOGRAPHY_SKILLS.LOCATIONAL,
    GEOGRAPHY_SKILLS.MAP_SKILLS,
    GEOGRAPHY_SKILLS.EXAM,
  ],
  commonMistakes: topic.commonMistakes || sectionCommonMistakes,
  quickCheck: topic.quickCheck || [],
  keywords: topic.keywords || [],
  retrievalStrength: topic.retrievalStrength || 'core',
  ...topic,
});

export const geographyContent1 = {
  id: 1,
  title: 'Locational Knowledge',
  slug: 'locational-knowledge',
  color: 'cyan',
  icon: 'Globe',
  description:
    'The foundation section for Geography: continents, oceans, hemispheres, latitude and longitude, and key world regions including Africa, India, China, Russia and the Middle East.',
  curriculumLinks: [
    'Extend locational knowledge and deepen spatial awareness of the world’s countries',
    'Use maps of the world to focus on Africa, Russia, Asia including China and India, and the Middle East',
    'Identify environmental regions including polar, desert, tropical and temperate regions',
    'Understand key physical and human characteristics, countries and major cities',
    'Interpret maps, atlases, globes, aerial images and Geographical Information Systems',
    'Communicate geographical information using maps, data and extended writing',
  ],
  sectionSkills: [
    GEOGRAPHY_SKILLS.LOCATIONAL,
    GEOGRAPHY_SKILLS.MAP_SKILLS,
    GEOGRAPHY_SKILLS.PLACE,
    GEOGRAPHY_SKILLS.PHYSICAL,
    GEOGRAPHY_SKILLS.HUMAN,
    GEOGRAPHY_SKILLS.DATA,
  ],
  topics: [
    makeTopic({
      id: 'geo-1-1',
      slug: 'our-world-continents-oceans-and-hemispheres',
      title: 'Our World: Continents, Oceans and Hemispheres',
      summary:
        'Build secure world-map knowledge by learning the seven continents, five oceans, key lines of latitude and longitude, and the way geographers divide the world into hemispheres.',
      keywords: [
        'continent',
        'ocean',
        'Equator',
        'Prime Meridian',
        'latitude',
        'longitude',
        'hemisphere',
        'Tropic of Cancer',
        'Tropic of Capricorn',
      ],
      examSkills: [
        GEOGRAPHY_SKILLS.LOCATIONAL,
        GEOGRAPHY_SKILLS.MAP_SKILLS,
        GEOGRAPHY_SKILLS.EXAM,
      ],
      quickCheck: [
        'Can you name the seven continents?',
        'Can you name the five oceans?',
        'What is the difference between latitude and longitude?',
        'Which line divides the Northern and Southern Hemispheres?',
      ],
      commonMistakes: [
        'Calling the UK a continent',
        'Forgetting Australasia/Oceania',
        'Mixing up the Arctic Ocean with Antarctica',
        'Saying longitude runs east-west instead of north-south',
      ],
      explainer: {
        title: 'Our World: Continents, Oceans and Hemispheres',
        content: `
Geography begins with knowing where places are. A strong mental map helps you understand every later topic: climate, development, trade, hazards, migration and ecosystems.

The world has seven continents:
- Africa
- Antarctica
- Asia
- Australasia/Oceania
- Europe
- North America
- South America

The world has five major oceans:
- Pacific Ocean
- Atlantic Ocean
- Indian Ocean
- Southern Ocean
- Arctic Ocean

The Pacific Ocean is the largest ocean. The Arctic Ocean is the smallest. Antarctica is a continent, not an ocean.

Geographers divide the Earth using imaginary lines.

The Equator is 0° latitude. It divides the Earth into the Northern Hemisphere and Southern Hemisphere.

The Prime Meridian is 0° longitude. It passes through Greenwich in London and helps divide the Earth into the Eastern Hemisphere and Western Hemisphere.

Latitude lines run east-west around the globe, but they measure distance north or south of the Equator.

Longitude lines run north-south from pole to pole, but they measure distance east or west of the Prime Meridian.

Important lines of latitude include:
- Equator: 0°
- Tropic of Cancer: 23.5°N
- Tropic of Capricorn: 23.5°S
- Arctic Circle: 66.5°N
- Antarctic Circle: 66.5°S

These lines matter because they help explain climate. Places close to the Equator usually receive more direct sunlight and are generally warmer. Places closer to the poles receive less direct sunlight and are colder.

Grade 9 geographical thinking goes beyond naming places. You explain why location matters.
`,
      },
      interactiveLesson: {
        title: 'Build Your World Map',
        steps: [
          {
            type: 'info',
            content:
              'Start by building a secure mental map. This makes later GCSE topics much easier.',
          },
          {
            type: 'question',
            question: 'Which is the largest ocean?',
            options: ['Atlantic Ocean', 'Indian Ocean', 'Pacific Ocean', 'Arctic Ocean'],
            correct: 2,
            explanation: 'The Pacific Ocean is the largest ocean by area.',
          },
          {
            type: 'question',
            question: 'The Equator is a line of:',
            options: ['Latitude', 'Longitude'],
            correct: 0,
            explanation: 'The Equator is 0° latitude and divides the Earth into north and south.',
          },
          {
            type: 'question',
            question: 'The Prime Meridian passes through:',
            options: ['New York', 'Greenwich', 'Tokyo', 'Cairo'],
            correct: 1,
            explanation: 'The Prime Meridian is 0° longitude and passes through Greenwich in London.',
          },
        ],
      },
      workedExamples: [
        {
          label: 'Example 1 — Latitude and climate',
          prompt:
            'Question: Explain why places close to the Equator are generally warmer than places close to the poles.',
          tiers: [
            tier(
              'Weak',
              'Places near the Equator are hotter because they are in the middle of the world.',
              'This is partly on the right track but vague. “Middle of the world” is not precise geographical explanation.'
            ),
            tier(
              'Good',
              'Places near the Equator are generally warmer because they receive more direct sunlight throughout the year.',
              'Clear and accurate. It links location to sunlight.'
            ),
            tier(
              'Excellent',
              'Places close to the Equator are generally warmer because the Sun’s rays strike the Earth more directly there, concentrating energy over a smaller surface area. Near the poles, the Sun’s rays arrive at a lower angle, spreading the same energy over a larger area, so temperatures are lower.',
              'This gives a causal explanation using precise geographical reasoning.'
            ),
          ],
        },
        {
          label: 'Example 2 — Describing global location',
          prompt:
            'Question: Describe the location of Europe using geographical language.',
          tiers: [
            tier(
              'Weak',
              'Europe is near Asia and above Africa.',
              'Some basic locational awareness, but “above” is imprecise. Use compass directions.'
            ),
            tier(
              'Good',
              'Europe lies mostly in the Northern Hemisphere. It is north of Africa and west of Asia.',
              'Clearer because it uses hemispheres and compass directions.'
            ),
            tier(
              'Excellent',
              'Europe lies mainly in the Northern and Eastern Hemispheres, although parts of western Europe are west of the Prime Meridian. It is north of Africa, west of Asia and bordered by the Atlantic Ocean to the west and the Arctic Ocean to the north.',
              'Precise, layered and supported by named reference points.'
            ),
          ],
        },
      ],
      practice: [
        {
          type: 'mcq',
          question: 'Which continent contains the UK, France and Germany?',
          options: ['Asia', 'Europe', 'Africa', 'South America'],
          correct: 1,
        },
        {
          type: 'mcq',
          question: 'Which line of latitude is at 0°?',
          options: ['Tropic of Cancer', 'Prime Meridian', 'Equator', 'Arctic Circle'],
          correct: 2,
        },
        {
          type: 'short-answer',
          question: 'Name the largest ocean in the world.',
          answer: 'Pacific Ocean',
          acceptedAnswers: ['Pacific', 'the Pacific', 'Pacific Ocean'],
        },
        {
          type: 'true-false',
          question: 'Longitude measures distance east or west of the Prime Meridian.',
          correct: true,
        },
        {
          type: 'true-false',
          question: 'Latitude lines run north-south from pole to pole.',
          correct: false,
        },
      ],
      applicationTask: {
        title: 'Application Task',
        prompt:
          'Describe the location of the UK in 3–4 sentences. Use hemisphere, continent, ocean and compass direction vocabulary.',
        sentenceStarter: 'The UK is located...',
        hints: [
          'Mention Europe.',
          'Mention the Northern Hemisphere.',
          'Use compass directions such as west, north-west or east.',
          'Name at least one surrounding sea or ocean.',
        ],
      },
      aiFeedback: {
        successCriteria: [
          'Uses accurate continent and hemisphere vocabulary',
          'Uses compass directions',
          'Names at least one ocean or sea',
          'Avoids vague wording such as “above” or “next to”',
        ],
      },
      improvementTask: {
        title: 'Improve Your Answer',
        prompt:
          'Improve your answer by replacing vague words such as “above” and “near” with precise compass directions.',
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt:
          'Explain why latitude is more useful than continent name when predicting broad climate patterns.',
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'geo-1-2',
      slug: 'africa-physical-and-political-geography',
      title: 'Africa: Physical and Political Geography',
      summary:
        'Understand Africa as a major world region by locating its countries, deserts, rivers, mountains, climate zones and population patterns.',
      keywords: [
        'Africa',
        'Sahara',
        'Nile',
        'Congo Basin',
        'Kilimanjaro',
        'Nigeria',
        'Egypt',
        'Kenya',
        'South Africa',
        'Sahel',
      ],
      examSkills: [
        GEOGRAPHY_SKILLS.LOCATIONAL,
        GEOGRAPHY_SKILLS.PHYSICAL,
        GEOGRAPHY_SKILLS.HUMAN,
        GEOGRAPHY_SKILLS.PLACE,
      ],
      quickCheck: [
        'Which desert dominates North Africa?',
        'Which river flows north through Egypt?',
        'Which African country has the largest population?',
        'How does climate vary from North Africa to Central Africa?',
      ],
      commonMistakes: [
        'Treating Africa as one country rather than a continent',
        'Assuming all of Africa is desert',
        'Forgetting that the Equator crosses central Africa',
        'Confusing Nigeria with Niger',
      ],
      explainer: {
        title: 'Africa: Physical and Political Geography',
        content: `
Africa is the second-largest continent by area and population. It contains 54 countries and a huge variety of landscapes, climates, cultures and economies.

Key physical features:
- Sahara Desert: the largest hot desert in the world, across North Africa
- Sahel: semi-arid transition zone south of the Sahara
- Congo Basin: tropical rainforest region in Central Africa
- River Nile: flows north through countries including Sudan and Egypt
- River Congo: major equatorial river in Central Africa
- River Niger: important river in West Africa
- River Zambezi: flows through southern Africa and includes Victoria Falls
- Atlas Mountains: north-west Africa
- Mount Kilimanjaro: Africa’s highest mountain, in Tanzania
- Great Rift Valley: major geological feature running through East Africa

Key countries to know:
- Nigeria: Africa’s most populous country
- Egypt: north-east Africa, strongly linked to the River Nile
- Kenya: East Africa, crossed by the Equator
- Ethiopia: highland country in East Africa
- South Africa: southern tip of the continent
- Democratic Republic of the Congo: large central African country containing rainforest

Africa is not one simple environment. North Africa is dominated by desert, Central Africa has rainforest, East Africa contains mountains and rift valleys, and parts of southern Africa have savanna, desert and Mediterranean-style climates.

Human geography also varies. Some African countries are rapidly urbanising. Lagos in Nigeria is one of the world’s largest and fastest-growing cities. Africa contains some of the world’s youngest populations, meaning a high proportion of people are children or young adults.

A strong geographer avoids stereotypes. Instead of saying “Africa is poor” or “Africa is hot”, use precise regional evidence.
`,
      },
      interactiveLesson: {
        title: 'Africa: Spot the Region',
        steps: [
          {
            type: 'question',
            question: 'Which desert dominates North Africa?',
            options: ['Gobi', 'Sahara', 'Atacama', 'Kalahari'],
            correct: 1,
            explanation: 'The Sahara is the largest hot desert in the world.',
          },
          {
            type: 'question',
            question: 'Which river is strongly linked to Egypt?',
            options: ['Nile', 'Amazon', 'Yangtze', 'Volga'],
            correct: 0,
            explanation: 'The Nile flows north through Egypt to the Mediterranean Sea.',
          },
          {
            type: 'question',
            question: 'Africa is best described as:',
            options: ['One country', 'A continent containing many countries and environments'],
            correct: 1,
            explanation: 'Africa is a continent with 54 countries and major environmental variation.',
          },
        ],
      },
      workedExamples: [
        {
          label: 'Example 1 — Avoiding stereotypes',
          prompt:
            'Question: Explain why it is inaccurate to describe Africa as “mostly desert and empty”.',
          tiers: [
            tier(
              'Weak',
              'Africa is not all desert.',
              'Correct but too brief. It needs evidence.'
            ),
            tier(
              'Good',
              'Africa is not all desert because central Africa contains tropical rainforest in the Congo Basin and many areas have savanna or highland environments.',
              'Accurate and supported with named physical regions.'
            ),
            tier(
              'Excellent',
              'Describing Africa as “mostly desert and empty” is inaccurate because Africa contains diverse environments. The Sahara dominates much of North Africa, but the Congo Basin contains tropical rainforest, East Africa includes highlands and the Great Rift Valley, and many countries have large cities and dense populations, such as Lagos in Nigeria.',
              'Strong because it challenges the claim using physical and human geography.'
            ),
          ],
        },
        {
          label: 'Example 2 — Physical geography and population',
          prompt:
            'Question: Suggest why many settlements in Egypt are close to the River Nile.',
          tiers: [
            tier(
              'Weak',
              'People need water.',
              'True, but very basic.'
            ),
            tier(
              'Good',
              'Many settlements in Egypt are close to the Nile because it provides water in an otherwise dry desert environment.',
              'Clear explanation linking river and desert.'
            ),
            tier(
              'Excellent',
              'Many settlements in Egypt are concentrated along the Nile because the surrounding Sahara is extremely dry, while the river provides water for drinking, irrigation, transport and farming. This creates a narrow but valuable corridor of settlement through a largely arid landscape.',
              'Precise and multi-causal, linking physical and human geography.'
            ),
          ],
        },
      ],
      practice: [
        {
          type: 'mcq',
          question: 'Which is the largest hot desert in the world?',
          options: ['Gobi', 'Sahara', 'Thar', 'Atacama'],
          correct: 1,
        },
        {
          type: 'mcq',
          question: 'Which African country has the largest population?',
          options: ['Kenya', 'Nigeria', 'Egypt', 'South Africa'],
          correct: 1,
        },
        {
          type: 'short-answer',
          question: 'Name Africa’s highest mountain.',
          answer: 'Kilimanjaro',
          acceptedAnswers: ['Mount Kilimanjaro', 'Kilimanjaro'],
        },
        {
          type: 'true-false',
          question: 'The Equator crosses central Africa.',
          correct: true,
        },
        {
          type: 'true-false',
          question: 'Africa is a country.',
          correct: false,
        },
      ],
      applicationTask: {
        title: 'Application Task',
        prompt:
          'Write a short paragraph explaining how Africa contains contrasting physical environments. Use at least three named examples.',
        sentenceStarter: 'Africa contains contrasting physical environments because...',
        hints: [
          'Use Sahara Desert.',
          'Use Congo Basin.',
          'Use Kilimanjaro, the Nile, the Sahel or the Great Rift Valley.',
          'Avoid generalisations.',
        ],
      },
      aiFeedback: {
        successCriteria: [
          'Uses at least three named examples',
          'Explains contrast rather than listing only',
          'Uses accurate physical geography vocabulary',
          'Avoids stereotypes',
        ],
      },
      improvementTask: {
        title: 'Improve Your Answer',
        prompt:
          'Improve your paragraph by adding one human geography example, such as Lagos, Nigeria or Nile settlement.',
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt:
          'Explain how the River Nile shows the interaction between physical geography and human settlement.',
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'geo-1-3',
      slug: 'asia-india-location-climate-and-people',
      title: 'Asia: India — Location, Climate and People',
      summary:
        'Explore India as a major Asian country by studying its location, mountains, rivers, monsoon climate, population and major cities.',
      keywords: [
        'India',
        'Asia',
        'Himalayas',
        'Ganges',
        'Brahmaputra',
        'monsoon',
        'Delhi',
        'Mumbai',
        'Kolkata',
        'Bengaluru',
      ],
      examSkills: [
        GEOGRAPHY_SKILLS.LOCATIONAL,
        GEOGRAPHY_SKILLS.PLACE,
        GEOGRAPHY_SKILLS.PHYSICAL,
        GEOGRAPHY_SKILLS.HUMAN,
        GEOGRAPHY_SKILLS.INTERCONNECTION,
      ],
      quickCheck: [
        'Which mountain range lies north of India?',
        'What is a monsoon?',
        'Why are the Ganges and Brahmaputra important?',
        'Why is India important in global geography?',
      ],
      commonMistakes: [
        'Calling India a continent',
        'Assuming India has only one climate',
        'Confusing Mumbai with the capital city',
        'Forgetting the importance of the monsoon',
      ],
      explainer: {
        title: 'India: Location, Climate and People',
        content: `
India is a country in South Asia. It is one of the world’s largest countries by area and has the world’s largest population.

India’s location matters. It is bordered by the Indian Ocean to the south, Pakistan to the west, China and Nepal to the north, and Bangladesh and Myanmar to the east.

Major physical features:
- Himalayas: huge mountain range to the north
- Ganges River: important river flowing across northern India
- Brahmaputra River: major river flowing through north-east India and Bangladesh
- Thar Desert: dry region in north-west India
- Deccan Plateau: large raised area in central and southern India
- Indian Ocean: major ocean influencing trade, climate and settlement

India has a monsoon climate. The summer monsoon brings heavy rain, especially from June to September. This rain is vital for farming, water supply and rivers. However, too much monsoon rain can cause flooding, while a weak monsoon can cause drought and crop failure.

Major cities:
- Delhi: capital city
- Mumbai: financial centre and major port
- Kolkata: major city in eastern India
- Bengaluru: technology and service-industry centre
- Chennai: major city on the south-east coast

Human geography:
India has a very large and diverse population. It contains huge cities, rural farming areas, rapidly growing industries and major technology sectors. India is often described as a newly emerging economy because its economic power and global influence have grown rapidly.

Grade 9 thinking links physical and human geography. For example, the monsoon is not just weather: it affects farming, water supply, flooding, food prices, cities and livelihoods.
`,
      },
      interactiveLesson: {
        title: 'India: Physical and Human Links',
        steps: [
          {
            type: 'question',
            question: 'Which mountain range lies to the north of India?',
            options: ['Andes', 'Himalayas', 'Alps', 'Atlas Mountains'],
            correct: 1,
          },
          {
            type: 'question',
            question: 'Which city is India’s capital?',
            options: ['Mumbai', 'Delhi', 'Kolkata', 'Bengaluru'],
            correct: 1,
          },
          {
            type: 'question',
            question: 'The monsoon is especially important because it affects:',
            options: ['Only tourism', 'Farming, water supply and flood risk'],
            correct: 1,
          },
        ],
      },
      workedExamples: [
        {
          label: 'Example 1 — Explaining the monsoon',
          prompt:
            'Question: Explain why the monsoon is important for India.',
          tiers: [
            tier(
              'Weak',
              'The monsoon is rain and India needs rain.',
              'Accurate but too simple.'
            ),
            tier(
              'Good',
              'The monsoon is important because it brings heavy summer rainfall that supports farming and water supply.',
              'Clear and relevant.'
            ),
            tier(
              'Excellent',
              'The monsoon is important because heavy summer rainfall supports agriculture, replenishes rivers and reservoirs, and provides water for millions of people. However, it also creates risk: intense rainfall can cause flooding, while a weak monsoon can lead to drought and crop failure.',
              'Balanced explanation with benefits and risks.'
            ),
          ],
        },
        {
          label: 'Example 2 — Location and development',
          prompt:
            'Question: Suggest why India’s location helps it connect to the wider world.',
          tiers: [
            tier(
              'Weak',
              'India is near the sea.',
              'Basic but undeveloped.'
            ),
            tier(
              'Good',
              'India’s coastline on the Indian Ocean helps it trade with other countries by sea.',
              'Clear link between location and trade.'
            ),
            tier(
              'Excellent',
              'India’s position on the Indian Ocean gives it access to major global shipping routes, supporting trade through ports such as Mumbai and Chennai. Its location between the Middle East, East Africa and South East Asia also helps connect India to wider economic networks.',
              'Stronger because it names places and explains interconnection.'
            ),
          ],
        },
      ],
      practice: [
        {
          type: 'mcq',
          question: 'Which mountain range forms India’s northern edge?',
          options: ['Himalayas', 'Andes', 'Urals', 'Rockies'],
          correct: 0,
        },
        {
          type: 'mcq',
          question: 'Which city is India’s capital?',
          options: ['Mumbai', 'Delhi', 'Kolkata', 'Chennai'],
          correct: 1,
        },
        {
          type: 'short-answer',
          question: 'Name one major river in India.',
          answer: 'Ganges',
          acceptedAnswers: ['Ganges', 'Ganga', 'Brahmaputra'],
        },
        {
          type: 'true-false',
          question: 'A monsoon can bring both benefits and risks.',
          correct: true,
        },
        {
          type: 'true-false',
          question: 'India is a continent.',
          correct: false,
        },
      ],
      applicationTask: {
        title: 'Application Task',
        prompt:
          'Explain how India’s physical geography affects people. Use the Himalayas, rivers or monsoon rainfall in your answer.',
        sentenceStarter: 'India’s physical geography affects people because...',
        hints: [
          'Link one physical feature to one human activity.',
          'Use words such as farming, water supply, flooding, trade or settlement.',
          'Try to include both benefit and risk.',
        ],
      },
      aiFeedback: {
        successCriteria: [
          'Names at least one physical feature',
          'Links physical geography to human activity',
          'Explains cause and effect',
          'Uses precise place evidence',
        ],
      },
      improvementTask: {
        title: 'Improve Your Answer',
        prompt:
          'Improve your answer by adding one specific Indian city or river.',
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt:
          'Explain why the monsoon is both a resource and a hazard for India.',
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'geo-1-4',
      slug: 'asia-china-rivers-population-and-industry',
      title: 'Asia: China — Rivers, Population and Industry',
      summary:
        'Understand China’s location, major physical regions, rivers, population distribution, major cities and role in global manufacturing.',
      keywords: [
        'China',
        'Asia',
        'Yangtze',
        'Yellow River',
        'Gobi Desert',
        'Tibetan Plateau',
        'Beijing',
        'Shanghai',
        'manufacturing',
        'population distribution',
      ],
      examSkills: [
        GEOGRAPHY_SKILLS.LOCATIONAL,
        GEOGRAPHY_SKILLS.PLACE,
        GEOGRAPHY_SKILLS.PHYSICAL,
        GEOGRAPHY_SKILLS.HUMAN,
        GEOGRAPHY_SKILLS.INTERCONNECTION,
      ],
      quickCheck: [
        'Which river is the longest in Asia?',
        'Where is China’s population most concentrated?',
        'What is China’s capital city?',
        'Why is China important in global manufacturing?',
      ],
      commonMistakes: [
        'Confusing Beijing and Shanghai',
        'Assuming China’s population is spread evenly',
        'Forgetting western China is much more mountainous and sparsely populated',
        'Ignoring the importance of rivers and coastal cities',
      ],
      explainer: {
        title: 'China: Rivers, Population and Industry',
        content: `
China is a huge country in East Asia. It has some of the world’s most important physical features, cities and economic regions.

Major physical features:
- Yangtze River: the longest river in Asia
- Yellow River: historically important river in northern China
- Gobi Desert: dry region in northern China and Mongolia
- Tibetan Plateau: high-altitude region in western China
- Himalayas: mountains on China’s south-western edge
- East China Sea and South China Sea: important coastal waters

Major cities:
- Beijing: capital city
- Shanghai: huge port city and global financial centre
- Guangzhou and Shenzhen: major manufacturing and trade cities in southern China
- Chongqing: major inland city on the Yangtze River

Population distribution:
China’s population is not evenly spread. Eastern China is much more densely populated because it has flatter land, major rivers, fertile lowlands, coastal access and large cities. Western China is generally more mountainous, higher, drier and less densely populated.

Economic geography:
China is often described as the “workshop of the world” because of its huge manufacturing sector. It produces electronics, machinery, clothing, vehicles and many consumer goods. Coastal cities and special economic zones helped China become central to global trade.

Grade 9 thinking connects physical geography with human geography. China’s rivers, coast and lowlands help explain where people live and where industry has grown.
`,
      },
      interactiveLesson: {
        title: 'China: Pattern and Explanation',
        steps: [
          {
            type: 'question',
            question: 'Which is the longest river in Asia?',
            options: ['Yellow River', 'Yangtze', 'Ganges', 'Mekong'],
            correct: 1,
          },
          {
            type: 'question',
            question: 'China’s population is most concentrated in the:',
            options: ['East and coastal lowlands', 'High western mountains'],
            correct: 0,
          },
          {
            type: 'question',
            question: 'Which city is China’s capital?',
            options: ['Shanghai', 'Beijing', 'Shenzhen', 'Hong Kong'],
            correct: 1,
          },
        ],
      },
      workedExamples: [
        {
          label: 'Example 1 — Population distribution',
          prompt:
            'Question: Explain why eastern China is more densely populated than western China.',
          tiers: [
            tier(
              'Weak',
              'More people live in eastern China because there are more cities.',
              'Partly true, but it does not explain why the cities are there.'
            ),
            tier(
              'Good',
              'Eastern China is more densely populated because it has flatter land, major rivers and better access to the coast.',
              'Clear and accurate.'
            ),
            tier(
              'Excellent',
              'Eastern China is more densely populated because flatter lowlands, fertile river valleys and coastal access make settlement, farming, transport and trade easier. Western China contains high mountains, deserts and plateaus, which make large-scale settlement and industry more difficult.',
              'Strong because it compares both regions and links physical geography to human activity.'
            ),
          ],
        },
        {
          label: 'Example 2 — Rivers and industry',
          prompt:
            'Question: Suggest why the Yangtze River is important to China.',
          tiers: [
            tier(
              'Weak',
              'It is a big river.',
              'Too vague. Size alone is not enough.'
            ),
            tier(
              'Good',
              'The Yangtze is important because it provides water, transport and supports cities and industry.',
              'Good multi-purpose explanation.'
            ),
            tier(
              'Excellent',
              'The Yangtze is important because it links inland and coastal China, supports major cities such as Chongqing and Shanghai, provides water for farming and industry, and acts as a transport route. This helps explain why the Yangtze corridor is one of China’s key economic regions.',
              'Precise and place-specific.'
            ),
          ],
        },
      ],
      practice: [
        {
          type: 'mcq',
          question: 'Which is China’s capital city?',
          options: ['Shanghai', 'Beijing', 'Guangzhou', 'Chongqing'],
          correct: 1,
        },
        {
          type: 'mcq',
          question: 'Which river is the longest in Asia?',
          options: ['Yangtze', 'Yellow River', 'Ganges', 'Indus'],
          correct: 0,
        },
        {
          type: 'short-answer',
          question: 'Name one desert in northern China.',
          answer: 'Gobi Desert',
          acceptedAnswers: ['Gobi', 'Gobi Desert'],
        },
        {
          type: 'true-false',
          question: 'China’s population is evenly spread across the whole country.',
          correct: false,
        },
        {
          type: 'true-false',
          question: 'Shanghai is an important port and financial centre.',
          correct: true,
        },
      ],
      applicationTask: {
        title: 'Application Task',
        prompt:
          'Explain why China’s eastern coastal region is important for population and industry.',
        sentenceStarter: 'China’s eastern coastal region is important because...',
        hints: [
          'Mention flatter land.',
          'Mention coastal access.',
          'Mention rivers, ports or trade.',
          'Name Beijing, Shanghai or Shenzhen if relevant.',
        ],
      },
      aiFeedback: {
        successCriteria: [
          'Explains population distribution',
          'Links physical geography to economic activity',
          'Uses named place evidence',
          'Uses precise geographical vocabulary',
        ],
      },
      improvementTask: {
        title: 'Improve Your Answer',
        prompt:
          'Improve your answer by adding a comparison with western China.',
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt:
          'Explain why China’s physical geography helps create uneven development within the country.',
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'geo-1-5',
      slug: 'russia-scale-climate-and-resources',
      title: 'Russia: Scale, Climate and Resources',
      summary:
        'Study Russia’s vast scale, its position across Europe and Asia, Siberia, permafrost, taiga, major cities and natural resources.',
      keywords: [
        'Russia',
        'Siberia',
        'Ural Mountains',
        'taiga',
        'permafrost',
        'Moscow',
        'oil',
        'natural gas',
        'Arctic',
        'continental climate',
      ],
      examSkills: [
        GEOGRAPHY_SKILLS.LOCATIONAL,
        GEOGRAPHY_SKILLS.PHYSICAL,
        GEOGRAPHY_SKILLS.HUMAN,
        GEOGRAPHY_SKILLS.INTERCONNECTION,
        GEOGRAPHY_SKILLS.EXAM,
      ],
      quickCheck: [
        'Which mountain range divides European and Asian Russia?',
        'What is permafrost?',
        'Why is Siberia sparsely populated?',
        'Why are oil and gas important to Russia?',
      ],
      commonMistakes: [
        'Forgetting Russia is in both Europe and Asia',
        'Thinking Siberia is a country',
        'Confusing taiga with tundra',
        'Assuming large area automatically means high population density',
      ],
      explainer: {
        title: 'Russia: Scale, Climate and Resources',
        content: `
Russia is the largest country in the world by area. It stretches across eastern Europe and northern Asia and spans many time zones.

Key locational features:
- Moscow: capital city, in western Russia
- St Petersburg: major city in north-west Russia
- Ural Mountains: traditional boundary between European Russia and Asian Russia
- Siberia: huge region east of the Urals
- Arctic Ocean: north of Russia
- Pacific Ocean: east of Russia

Physical geography:
Much of Russia has a cold continental climate. Siberia is known for extremely cold winters, vast forests and low population density.

Taiga is coniferous forest found across large parts of Russia. It contains trees such as pine, spruce and fir.

Permafrost is permanently frozen ground. It affects buildings, roads, pipelines and settlement because the ground can become unstable if it thaws.

Human geography:
Russia’s population is unevenly distributed. Most people live in western Russia, where the climate is less extreme and the major cities and transport links are concentrated.

Resources:
Russia is a major exporter of oil and natural gas. These resources bring income and global influence, but they also create environmental challenges and economic dependence on fossil fuels.

Grade 9 thinking links scale, climate, resources and settlement. Russia’s size gives it resources and influence, but its cold climate and permafrost make development difficult in many regions.
`,
      },
      interactiveLesson: {
        title: 'Russia: Scale and Challenge',
        steps: [
          {
            type: 'question',
            question: 'Which mountain range divides European and Asian Russia?',
            options: ['Alps', 'Urals', 'Himalayas', 'Atlas'],
            correct: 1,
          },
          {
            type: 'question',
            question: 'Permafrost means:',
            options: ['Permanently frozen ground', 'A hot dry wind'],
            correct: 0,
          },
          {
            type: 'question',
            question: 'Most of Russia’s population lives in:',
            options: ['Western Russia', 'Far eastern Siberia'],
            correct: 0,
          },
        ],
      },
      workedExamples: [
        {
          label: 'Example 1 — Population distribution',
          prompt:
            'Question: Explain why Siberia is sparsely populated.',
          tiers: [
            tier(
              'Weak',
              'Siberia is cold.',
              'True, but too limited.'
            ),
            tier(
              'Good',
              'Siberia is sparsely populated because it has extremely cold winters, long distances and areas of permafrost.',
              'Clear, with several reasons.'
            ),
            tier(
              'Excellent',
              'Siberia is sparsely populated because its extreme continental climate makes daily life, farming and construction difficult. Permafrost creates unstable ground for buildings and roads, while huge distances make transport and services expensive. As a result, population is concentrated more heavily in western Russia.',
              'Strong because it links climate, ground conditions, infrastructure and distribution.'
            ),
          ],
        },
        {
          label: 'Example 2 — Resources and power',
          prompt:
            'Question: Suggest how oil and natural gas influence Russia’s economy.',
          tiers: [
            tier(
              'Weak',
              'Russia sells oil and gas.',
              'Basic statement, no explanation.'
            ),
            tier(
              'Good',
              'Oil and gas are important because Russia exports them and earns money from selling energy to other countries.',
              'Clear economic explanation.'
            ),
            tier(
              'Excellent',
              'Oil and natural gas influence Russia’s economy because export income supports government revenue, jobs and global trade links. They also give Russia geopolitical influence because other countries may depend on Russian energy supplies. However, dependence on fossil fuels can make the economy vulnerable to price changes and environmental pressure.',
              'Balanced, detailed and evaluative.'
            ),
          ],
        },
      ],
      practice: [
        {
          type: 'mcq',
          question: 'Which is the largest country in the world by area?',
          options: ['Canada', 'China', 'Russia', 'USA'],
          correct: 2,
        },
        {
          type: 'mcq',
          question: 'Which biome covers large parts of Russia?',
          options: ['Taiga', 'Tropical rainforest', 'Savanna', 'Mediterranean scrub'],
          correct: 0,
        },
        {
          type: 'short-answer',
          question: 'What is permanently frozen ground called?',
          answer: 'permafrost',
          acceptedAnswers: ['permafrost'],
        },
        {
          type: 'true-false',
          question: 'Russia is located in both Europe and Asia.',
          correct: true,
        },
        {
          type: 'true-false',
          question: 'Siberia is a separate country.',
          correct: false,
        },
      ],
      applicationTask: {
        title: 'Application Task',
        prompt:
          'Explain how Russia’s physical geography affects population distribution and economic activity.',
        sentenceStarter: 'Russia’s physical geography affects people because...',
        hints: [
          'Mention cold climate.',
          'Mention permafrost or taiga.',
          'Mention oil and natural gas.',
          'Compare western Russia and Siberia.',
        ],
      },
      aiFeedback: {
        successCriteria: [
          'Links physical geography to settlement',
          'Links resources to economic activity',
          'Uses accurate place evidence',
          'Explains rather than lists',
        ],
      },
      improvementTask: {
        title: 'Improve Your Answer',
        prompt:
          'Improve your answer by adding one sentence about why distance and transport are important in Russia.',
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt:
          'To what extent is Russia’s size an advantage? Give one benefit and one challenge.',
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'geo-1-6',
      slug: 'middle-east-location-oil-and-water',
      title: 'The Middle East: Location, Oil and Water',
      summary:
        'Understand the Middle East as a strategic world region shaped by location, oil, desert climate, water scarcity and major transport routes.',
      keywords: [
        'Middle East',
        'Persian Gulf',
        'Saudi Arabia',
        'Iran',
        'Iraq',
        'UAE',
        'oil',
        'desert climate',
        'water scarcity',
        'Suez Canal',
      ],
      examSkills: [
        GEOGRAPHY_SKILLS.LOCATIONAL,
        GEOGRAPHY_SKILLS.PLACE,
        GEOGRAPHY_SKILLS.HUMAN,
        GEOGRAPHY_SKILLS.PHYSICAL,
        GEOGRAPHY_SKILLS.INTERCONNECTION,
        GEOGRAPHY_SKILLS.EXAM,
      ],
      quickCheck: [
        'Where is the Middle East located?',
        'Why is oil important in the region?',
        'Why is water scarcity a major issue?',
        'What does the Suez Canal connect?',
      ],
      commonMistakes: [
        'Treating the Middle East as one country',
        'Assuming every Middle Eastern country is rich from oil',
        'Forgetting the importance of water scarcity',
        'Confusing the Persian Gulf with the Red Sea',
      ],
      explainer: {
        title: 'The Middle East: Location, Oil and Water',
        content: `
The Middle East is a region at the meeting point of Europe, Asia and Africa. It is not a continent and not one country. It includes countries such as Saudi Arabia, Iran, Iraq, Kuwait, Qatar, the United Arab Emirates, Oman, Yemen, Israel, Jordan, Lebanon, Syria and others depending on definition.

Location:
The region is strategically important because it sits between major continents and seas. It includes important routes such as the Suez Canal, which connects the Mediterranean Sea and the Red Sea. This route allows ships to travel between Europe and Asia without going all the way around Africa.

Physical geography:
Much of the Middle East has a hot desert or semi-arid climate. Rainfall is low and water scarcity is a major challenge. Rivers such as the Tigris and Euphrates are very important because they provide water for farming, cities and industry.

Oil and gas:
The Middle East contains some of the world’s most important oil and natural gas reserves, especially around the Persian Gulf. Oil exports have made some countries very wealthy and globally influential.

However, oil wealth is not evenly distributed. Some countries have huge oil income, while others face conflict, poverty or water stress. Oil also creates environmental challenges and future risks as the world shifts towards renewable energy.

Grade 9 thinking explains both importance and complexity. The Middle East matters because of resources, location, trade routes and water, but the region cannot be reduced to oil alone.
`,
      },
      interactiveLesson: {
        title: 'Middle East: Strategic Importance',
        steps: [
          {
            type: 'question',
            question: 'The Middle East lies at the meeting point of:',
            options: ['Europe, Asia and Africa', 'North America and South America'],
            correct: 0,
          },
          {
            type: 'question',
            question: 'The Suez Canal connects the Mediterranean Sea to the:',
            options: ['Red Sea', 'Arctic Ocean', 'Pacific Ocean', 'Caspian Sea'],
            correct: 0,
          },
          {
            type: 'question',
            question: 'A major environmental challenge in much of the Middle East is:',
            options: ['Water scarcity', 'Too much rainforest'],
            correct: 0,
          },
        ],
      },
      workedExamples: [
        {
          label: 'Example 1 — Strategic location',
          prompt:
            'Question: Explain why the Middle East has a strategically important location.',
          tiers: [
            tier(
              'Weak',
              'It is in the middle of places.',
              'The idea is there but it is vague and informal.'
            ),
            tier(
              'Good',
              'The Middle East is strategically important because it lies between Europe, Asia and Africa and contains important shipping routes.',
              'Accurate and relevant.'
            ),
            tier(
              'Excellent',
              'The Middle East has a strategically important location because it lies between Europe, Asia and Africa and controls or borders key transport routes, including the Suez Canal and Persian Gulf shipping lanes. This makes the region important for global trade, energy transport and political influence.',
              'Strong because it links location to trade, energy and power.'
            ),
          ],
        },
        {
          label: 'Example 2 — Oil and uneven development',
          prompt:
            'Question: Explain why oil can create both opportunities and challenges in the Middle East.',
          tiers: [
            tier(
              'Weak',
              'Oil makes countries rich.',
              'Too one-sided.'
            ),
            tier(
              'Good',
              'Oil creates opportunities because countries can export it and earn money, but it can also cause environmental damage.',
              'Balanced but still basic.'
            ),
            tier(
              'Excellent',
              'Oil creates opportunities by generating export income, jobs, infrastructure investment and global influence. However, it can also create challenges, including pollution, dependence on a single resource, uneven wealth distribution and vulnerability as global energy systems shift towards renewables.',
              'Balanced and evaluative, with several developed points.'
            ),
          ],
        },
      ],
      practice: [
        {
          type: 'mcq',
          question: 'Which sea does the Suez Canal connect to the Mediterranean Sea?',
          options: ['Red Sea', 'Black Sea', 'North Sea', 'Arabian Sea'],
          correct: 0,
        },
        {
          type: 'mcq',
          question: 'Which resource has strongly shaped the economies of Persian Gulf countries?',
          options: ['Oil', 'Timber', 'Coal only', 'Diamonds'],
          correct: 0,
        },
        {
          type: 'short-answer',
          question: 'Name one country in the Middle East.',
          answer: 'Saudi Arabia',
          acceptedAnswers: [
            'Saudi Arabia',
            'Iran',
            'Iraq',
            'Kuwait',
            'Qatar',
            'United Arab Emirates',
            'UAE',
            'Oman',
            'Yemen',
            'Jordan',
            'Israel',
            'Lebanon',
            'Syria',
          ],
        },
        {
          type: 'true-false',
          question: 'The Middle East is one country.',
          correct: false,
        },
        {
          type: 'true-false',
          question: 'Water scarcity is a major issue in many parts of the Middle East.',
          correct: true,
        },
      ],
      applicationTask: {
        title: 'Application Task',
        prompt:
          'Explain why the Middle East is important globally. Include location, oil and water in your answer.',
        sentenceStarter: 'The Middle East is globally important because...',
        hints: [
          'Mention Europe, Asia and Africa.',
          'Mention oil or gas.',
          'Mention water scarcity.',
          'Mention the Suez Canal or Persian Gulf if you can.',
        ],
      },
      aiFeedback: {
        successCriteria: [
          'Explains strategic location',
          'Explains the importance of oil or gas',
          'Mentions water scarcity',
          'Uses named place evidence',
        ],
      },
      improvementTask: {
        title: 'Improve Your Answer',
        prompt:
          'Improve your answer by adding one sentence explaining why oil wealth can be uneven.',
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt:
          'Explain why the Middle East should not be understood only through oil.',
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'geo-1-7',
      slug: 'locational-knowledge-knowledge-organiser',
      title: 'Locational Knowledge — Knowledge Organiser',
      summary:
        'Fast-reference summary for Section 1: world map knowledge, continents, oceans, latitude, longitude and key world regions.',
      keywords: [
        'knowledge organiser',
        'revision',
        'continents',
        'oceans',
        'latitude',
        'longitude',
        'Africa',
        'India',
        'China',
        'Russia',
        'Middle East',
      ],
      examSkills: [
        GEOGRAPHY_SKILLS.LOCATIONAL,
        GEOGRAPHY_SKILLS.MAP_SKILLS,
        GEOGRAPHY_SKILLS.DATA,
        GEOGRAPHY_SKILLS.EXAM,
      ],
      quickCheck: [
        'Can you name the seven continents?',
        'Can you explain the difference between latitude and longitude?',
        'Can you name one key physical feature in Africa, India, China, Russia and the Middle East?',
      ],
      commonMistakes: sectionCommonMistakes,
      explainer: {
        title: 'Knowledge Organiser',
        content: `
Use this summary to revise everything from Geography Section 1.

World map essentials:
- Seven continents: Africa, Antarctica, Asia, Australasia/Oceania, Europe, North America, South America.
- Five oceans: Pacific, Atlantic, Indian, Southern, Arctic.
- Pacific Ocean = largest ocean.
- Antarctica = continent.
- Arctic = ocean/region around the North Pole.

Latitude and longitude:
- Equator = 0° latitude.
- Prime Meridian = 0° longitude.
- Latitude lines run east-west but measure north/south.
- Longitude lines run north-south but measure east/west.
- Tropic of Cancer = 23.5°N.
- Tropic of Capricorn = 23.5°S.

Africa:
- Second-largest continent.
- Sahara Desert in North Africa.
- Congo Basin rainforest in Central Africa.
- River Nile flows through Egypt.
- Mount Kilimanjaro is Africa’s highest mountain.
- Nigeria is Africa’s most populous country.
- Avoid saying “Africa is a country” or “Africa is all desert”.

India:
- Located in South Asia.
- Himalayas to the north.
- Ganges and Brahmaputra are key rivers.
- Monsoon rainfall is vital for farming and water supply.
- Delhi is the capital.
- Mumbai is a major financial centre and port.

China:
- Located in East Asia.
- Yangtze River is the longest river in Asia.
- Yellow River is historically important.
- Gobi Desert in the north.
- Tibetan Plateau and Himalayas in the west/south-west.
- Beijing is the capital.
- Shanghai is a major port and financial centre.
- Population is concentrated more heavily in eastern China.

Russia:
- Largest country in the world by area.
- Spans Europe and Asia.
- Ural Mountains divide European and Asian Russia.
- Siberia is a vast region east of the Urals.
- Taiga = coniferous forest.
- Permafrost = permanently frozen ground.
- Russia is a major oil and natural gas exporter.

Middle East:
- Region at the meeting point of Europe, Asia and Africa.
- Much of the region has hot desert or semi-arid climate.
- Water scarcity is a major issue.
- Tigris and Euphrates are important rivers.
- Persian Gulf region is important for oil and gas.
- Suez Canal links the Mediterranean Sea and Red Sea.

Grade 9 move:
Do not only name places. Explain why location matters.

Weak:
“China is big.”

Better:
“China’s population is concentrated in the east because flatter land, river valleys and coastal access support farming, settlement, transport and industry.”

Best:
“China’s uneven physical geography helps explain uneven population distribution and development: eastern lowlands and coastal cities support trade and manufacturing, while western mountains, deserts and plateaus are less densely populated.”
`,
      },
      interactiveLesson: {
        title: 'Recall Quiz',
        steps: [
          {
            type: 'question',
            question: 'Which line is 0° latitude?',
            options: ['Equator', 'Prime Meridian'],
            correct: 0,
          },
          {
            type: 'question',
            question: 'Which river is strongly associated with Egypt?',
            options: ['Nile', 'Yangtze'],
            correct: 0,
          },
          {
            type: 'question',
            question: 'Which mountain range divides European and Asian Russia?',
            options: ['Urals', 'Himalayas'],
            correct: 0,
          },
        ],
      },
      workedExamples: [
        {
          label: 'Example 1 — Turning facts into explanation',
          prompt:
            'Fact: India has monsoon rainfall.\n\nTask: Turn this into a developed geographical explanation.',
          tiers: [
            tier(
              'Weak',
              'India has monsoon rain.',
              'This is a fact, not an explanation.'
            ),
            tier(
              'Good',
              'India’s monsoon rainfall is important because it provides water for farming.',
              'This links weather to human activity.'
            ),
            tier(
              'Excellent',
              'India’s monsoon rainfall is important because it provides water for farming, replenishes rivers and supports food production. However, if the monsoon is unusually heavy it can cause flooding, while a weak monsoon can create drought and crop failure.',
              'This is developed, balanced and geographical.'
            ),
          ],
        },
        {
          label: 'Example 2 — Regional comparison',
          prompt:
            'Question: Compare one physical feature of Russia and the Middle East.',
          tiers: [
            tier(
              'Weak',
              'Russia is cold and the Middle East is hot.',
              'Basic comparison but needs development.'
            ),
            tier(
              'Good',
              'Russia has large areas of cold taiga and permafrost, while much of the Middle East has hot desert or semi-arid climate.',
              'Clear comparison with better terminology.'
            ),
            tier(
              'Excellent',
              'Russia and the Middle East have contrasting physical challenges. In Russia, cold continental climates and permafrost make settlement, farming and infrastructure difficult across Siberia. In the Middle East, hot desert and semi-arid climates create water scarcity, making rivers, aquifers and desalination especially important.',
              'Strong because it links physical features to human consequences.'
            ),
          ],
        },
      ],
      practice: [
        {
          type: 'mcq',
          question: 'Which pair is correct?',
          options: [
            'Equator = 0° latitude',
            'Equator = 0° longitude',
            'Prime Meridian = 23.5°N',
            'Tropic of Cancer = 0° latitude',
          ],
          correct: 0,
        },
        {
          type: 'mcq',
          question: 'Which country is Africa’s most populous?',
          options: ['Nigeria', 'Egypt', 'Kenya', 'South Africa'],
          correct: 0,
        },
        {
          type: 'mcq',
          question: 'Which feature is associated with western/northern China?',
          options: ['Gobi Desert', 'Amazon Rainforest', 'Sahara Desert', 'Alps'],
          correct: 0,
        },
        {
          type: 'true-false',
          question: 'Russia is the world’s largest country by area.',
          correct: true,
        },
        {
          type: 'true-false',
          question: 'The Middle East is best understood as a single country.',
          correct: false,
        },
      ],
      applicationTask: {
        title: 'Application Task',
        prompt:
          'Choose two places from this section and compare them in one paragraph. Include at least one physical feature and one human feature.',
        sentenceStarter: 'A useful comparison can be made between...',
        hints: [
          'Possible pairs: India and China, Russia and the Middle East, Africa and India.',
          'Use one physical feature such as river, desert, mountain or climate.',
          'Use one human feature such as population, city, trade or resources.',
        ],
      },
      aiFeedback: {
        successCriteria: [
          'Compares two named places',
          'Uses physical geography evidence',
          'Uses human geography evidence',
          'Explains similarities or differences clearly',
        ],
      },
      improvementTask: {
        title: 'Improve Your Answer',
        prompt:
          'Improve by adding one sentence beginning: “This matters because…”',
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt:
          'Explain why strong locational knowledge helps students succeed in later GCSE topics such as hazards, development, ecosystems and resource management.',
        xpReward: 50,
      },
    }),
  ],
};

export const getGeographyContent1TopicCount = () => geographyContent1.topics.length;

export const getGeographyContent1TopicBySlug = (slug) =>
  geographyContent1.topics.find((topic) => topic.slug === slug);

export const getGeographyContent1TopicById = (id) =>
  geographyContent1.topics.find((topic) => topic.id === id);

export const getGeographyContent1Keywords = () =>
  Array.from(new Set(geographyContent1.topics.flatMap((topic) => topic.keywords || []))).sort();

export default geographyContent1;